// Hộp "Việc cần xử lý" (notification-service tổng hợp theo người, FSD.md §4.3): một nguồn GET /inbox cho chuông,
// số trên menu (badges theo module) và thẻ Tổng quan. Kiểu khai báo ở libs/contracts/src/notification (bên sở hữu).
import { useQuery } from '@tanstack/react-query';
import type { notification } from '@cathouse/contracts';
import { ApiError, http, qk, type RealtimeBinding } from '@/shared/api';
import { syncServerTime } from '@/shared/lib';
import { Icon } from '@/shared/ui';

export type InboxSeverity = notification.InboxSeverity;
export type InboxItem = notification.InboxItem;
export type InboxList = notification.Inbox;

const SEV_RANK: Record<InboxSeverity, number> = { bad: 0, warn: 1, violet: 2, info: 3 };
/** Sắp bad → warn → violet → info (giữ thứ tự server trong cùng mức), như collectAlerts của prototype. */
export const sortInbox = <T extends Pick<InboxItem, 'sev'>>(items: readonly T[]) => [...items].sort((a, b) => (SEV_RANK[a.sev] ?? 9) - (SEV_RANK[b.sev] ?? 9));
export const inboxCount = (a: Pick<InboxItem, 'count'>) => a.count ?? 1;
/** Σ count của các mục có `to` trùng đường dẫn module — chỉ dùng khi phản hồi thiếu `badges`. */
export function inboxCountsByPath(items: readonly Pick<InboxItem, 'to' | 'count'>[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const a of items) if (a.to) out[a.to] = (out[a.to] ?? 0) + inboxCount(a);
  return out;
}
/**
 * Số trên menu theo module id: `badges` của server (tính trên toàn bộ mục, kể cả khi `items` bị cắt theo limit);
 * thiếu ⇒ cộng `count` theo `to` === '/' + route.
 */
export function inboxBadges(d: Pick<InboxList, 'items'> & { badges?: Record<string, number> | null } | undefined, modules: readonly { id: string; route: string }[]): Record<string, number> {
  if (!d) return {};
  if (d.badges) return d.badges;
  const byPath = inboxCountsByPath(d.items ?? []);
  return Object.fromEntries(modules.flatMap((m) => (byPath[`/${m.route}`] ? [[m.id, byPath[`/${m.route}`]!]] : [])));
}

const sorted = (d: InboxList): InboxList => ({ ...d, items: sortInbox(d.items ?? []) });
/** Route chưa có (notification-service chưa nối vào gateway) ⇒ hỏi lại thưa (5 phút), không hỏi khi focus; tin SSE vẫn làm mới. */
const missing = (e: unknown) => e instanceof ApiError && e.status === 404;
export const INBOX_POLL_MS = 30_000;
export const INBOX_MISSING_POLL_MS = 5 * 60_000;

export const useInbox = (enabled = true) =>
  useQuery({
    queryKey: qk.inbox.all,
    queryFn: async () => {
      const d = await http.get<InboxList>('/inbox');
      syncServerTime(d?.serverTime);
      return d;
    },
    select: sorted,
    enabled,
    refetchInterval: (q) => (missing(q.state.error) ? INBOX_MISSING_POLL_MS : INBOX_POLL_MS),
    refetchOnWindowFocus: (q) => !missing(q.state.error),
  });

/** Tin SSE 'inbox' (có staffIds — chỉ người liên quan nhận) ⇒ làm mới hộp việc. */
export const inboxRealtime: RealtimeBinding = { topic: 'inbox', module: 'dashboard', keys: () => [qk.inbox.all] };

/**
 * Một mục hộp việc (.alert-row). variant 'menu' (chuông: nút, đóng menu khi bấm) · 'card' (thẻ Tổng quan: chữ đậm nhỏ
 * + mũi tên).
 */
export function InboxRow({ item, onOpen, variant = 'menu' }: { item: InboxItem; onOpen?: (item: InboxItem) => void; variant?: 'menu' | 'card' }) {
  const sev = (
    <span className={`sev ${item.sev}`}>
      <Icon n={item.icon} s={15} />
    </span>
  );
  if (variant === 'card')
    return (
      <div
        className="alert-row"
        role="button"
        tabIndex={0}
        onClick={() => onOpen?.(item)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen?.(item);
          }
        }}
      >
        {sev}
        <span className="grow">
          <div className="strong small">{item.title}</div>
          {item.sub && <div className="tiny muted">{item.sub}</div>}
        </span>
        <Icon n="right" s={14} />
      </div>
    );
  return (
    <button type="button" className="alert-row" data-close onClick={() => onOpen?.(item)}>
      {sev}
      <span className="grow">
        <span className="t">{item.title}</span>
        {item.sub && <span className="s">{item.sub}</span>}
      </span>
    </button>
  );
}
